'use client';

import { useState, useEffect } from 'react';
import ServiceCard from './ServiceCard';
import ServiceComparison from './ServiceComparison';
import ProcessWorkflow from './ProcessWorkflow';
import ServiceRecommendation from './ServiceRecommendation';
import TechnologyStack from './TechnologyStack';
import TestimonialCard from './TestimonialCard';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
  imageAlt: string;
  features: string[];
  technologies: string[];
  priceRange: string;
  deliveryTime: string;
  link: string;
  category: string;
}

interface ComparisonFeature {
  name: string;
  webDev: boolean;
  mobileDev: boolean;
  wordpress: boolean;
  shopify: boolean;
  uiux: boolean;
  seo: boolean;
}

interface WorkflowStep {
  id: number;
  title: string;
  description: string;
  icon: string;
  duration: string;
}

interface Question {
  id: number;
  question: string;
  options: {
    text: string;
    value: string;
  }[];
}

interface Technology {
  name: string;
  icon: string;
  iconAlt: string;
  proficiency: number;
  category: string;
}

interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  image: string;
  imageAlt: string;
  rating: number;
  text: string;
  service: string;
}

export default function ServicesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState<'services' | 'comparison' | 'process'>('services');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
  {
    id: '1',
    title: 'Web Development',
    description: 'Custom web applications built with modern technologies like React, Next.js, and Node.js for scalable and performant solutions.',
    icon: 'CodeBracketIcon',
    image: "https://images.unsplash.com/photo-1555066931-bf19f8fd1085",
    imageAlt: 'Modern laptop displaying colorful code editor with multiple programming languages on dark desk',
    features: [
    'Responsive design for all devices',
    'SEO-optimized architecture',
    'Fast loading performance',
    'Secure authentication systems',
    'API integration capabilities'],

    technologies: ['React', 'Next.js', 'Node.js', 'TypeScript', 'Tailwind CSS'],
    priceRange: '$5,000 - $25,000',
    deliveryTime: '8-12 weeks',
    link: '/contact',
    category: 'development'
  },
  {
    id: '2',
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications for iOS and Android with seamless user experiences and robust functionality.',
    icon: 'DevicePhoneMobileIcon',
    image: "https://images.unsplash.com/photo-1600180902576-8cc9196d1a27",
    imageAlt: 'Smartphone displaying modern mobile app interface with colorful UI elements on wooden surface',
    features: [
    'Cross-platform compatibility',
    'Native performance optimization',
    'Push notification integration',
    'Offline functionality support',
    'App store deployment assistance'],

    technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'],
    priceRange: '$10,000 - $50,000',
    deliveryTime: '12-16 weeks',
    link: '/contact',
    category: 'development'
  },
  {
    id: '3',
    title: 'WordPress Development',
    description: 'Custom WordPress websites with powerful CMS capabilities, perfect for content-rich sites and blogs with easy management.',
    icon: 'DocumentTextIcon',
    image: "https://images.unsplash.com/photo-1581467014352-d60ae46c69d5",
    imageAlt: 'Laptop showing WordPress dashboard with content management interface on white desk with coffee cup',
    features: [
    'Custom theme development',
    'Plugin integration and customization',
    'E-commerce capabilities with WooCommerce',
    'SEO optimization built-in',
    'Easy content management'],

    technologies: ['WordPress', 'PHP', 'MySQL', 'WooCommerce', 'ACF'],
    priceRange: '$3,000 - $15,000',
    deliveryTime: '4-8 weeks',
    link: '/contact',
    category: 'cms'
  },
  {
    id: '4',
    title: 'Shopify Development',
    description: 'Complete e-commerce solutions on Shopify platform with custom themes, payment integration, and inventory management.',
    icon: 'ShoppingCartIcon',
    image: "https://images.unsplash.com/photo-1649424221016-58857288854e",
    imageAlt: 'Shopping cart icon on tablet screen with credit cards and product boxes in background',
    features: [
    'Custom Shopify theme design',
    'Payment gateway integration',
    'Inventory management system',
    'Multi-currency support',
    'Marketing automation tools'],

    technologies: ['Shopify', 'Liquid', 'JavaScript', 'Shopify API', 'Apps'],
    priceRange: '$4,000 - $20,000',
    deliveryTime: '6-10 weeks',
    link: '/contact',
    category: 'ecommerce'
  },
  {
    id: '5',
    title: 'UI/UX Design',
    description: 'User-centered design solutions that enhance engagement, improve conversions, and create memorable digital experiences.',
    icon: 'PaintBrushIcon',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5',
    imageAlt: 'Designer workspace with color palette swatches, wireframe sketches, and digital tablet showing UI mockups',
    features: [
    'User research and personas',
    'Wireframing and prototyping',
    'Visual design and branding',
    'Usability testing',
    'Design system creation'],

    technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Principle'],
    priceRange: '$2,500 - $12,000',
    deliveryTime: '4-6 weeks',
    link: '/contact',
    category: 'design'
  },
  {
    id: '6',
    title: 'SEO & Digital Marketing',
    description: 'Comprehensive digital marketing strategies including SEO optimization, content marketing, and social media management.',
    icon: 'ChartBarIcon',
    image: "https://images.unsplash.com/photo-1657727534442-d436ab275ce2",
    imageAlt: 'Marketing analytics dashboard on computer screen showing graphs, charts, and SEO metrics',
    features: [
    'On-page and off-page SEO',
    'Keyword research and optimization',
    'Content strategy development',
    'Social media marketing',
    'Analytics and reporting'],

    technologies: ['Google Analytics', 'SEMrush', 'Ahrefs', 'Google Ads', 'Social Media'],
    priceRange: '$1,500 - $8,000/month',
    deliveryTime: 'Ongoing',
    link: '/contact',
    category: 'marketing'
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  { name: 'Custom Design', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: true, seo: false },
  { name: 'Responsive Layout', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: true, seo: false },
  { name: 'E-commerce Ready', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: false, seo: true },
  { name: 'CMS Integration', webDev: true, mobileDev: false, wordpress: true, shopify: true, uiux: false, seo: false },
  { name: 'SEO Optimization', webDev: true, mobileDev: false, wordpress: true, shopify: true, uiux: false, seo: true },
  { name: 'Analytics Setup', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: false, seo: true },
  { name: 'Payment Integration', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: false, seo: false },
  { name: 'User Authentication', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: false, seo: false },
  { name: 'API Development', webDev: true, mobileDev: true, wordpress: false, shopify: false, uiux: false, seo: false },
  { name: 'Cloud Hosting', webDev: true, mobileDev: true, wordpress: true, shopify: true, uiux: false, seo: false }];


  const workflowSteps: WorkflowStep[] = [
  {
    id: 1,
    title: 'Discovery & Planning',
    description: 'We analyze your requirements, define project scope, create wireframes, and establish timelines and milestones.',
    icon: 'LightBulbIcon',
    duration: '1-2 weeks'
  },
  {
    id: 2,
    title: 'Design & Prototyping',
    description: 'Our designers create visual mockups, interactive prototypes, and establish the design system for your project.',
    icon: 'PaintBrushIcon',
    duration: '2-3 weeks'
  },
  {
    id: 3,
    title: 'Development',
    description: 'Our developers build your solution using modern technologies, following best practices and coding standards.',
    icon: 'CodeBracketIcon',
    duration: '4-8 weeks'
  },
  {
    id: 4,
    title: 'Testing & QA',
    description: 'Comprehensive testing across devices, browsers, and scenarios to ensure quality and performance.',
    icon: 'CheckCircleIcon',
    duration: '1-2 weeks'
  },
  {
    id: 5,
    title: 'Launch & Deployment',
    description: 'We deploy your project to production, configure hosting, and ensure everything runs smoothly.',
    icon: 'RocketLaunchIcon',
    duration: '1 week'
  },
  {
    id: 6,
    title: 'Support & Maintenance',
    description: 'Ongoing support, updates, monitoring, and optimization to keep your solution running at peak performance.',
    icon: 'WrenchScrewdriverIcon',
    duration: 'Ongoing'
  }];


  const quizQuestions: Question[] = [
  {
    id: 1,
    question: 'What type of project are you planning?',
    options: [
    { text: 'Online store or e-commerce platform', value: 'ecommerce' },
    { text: 'Mobile application for iOS/Android', value: 'mobile' },
    { text: 'Content-rich website or blog', value: 'content' },
    { text: 'Custom web application', value: 'webapp' }]

  },
  {
    id: 2,
    question: 'What is your primary goal?',
    options: [
    { text: 'Sell products online', value: 'ecommerce' },
    { text: 'Engage mobile users', value: 'mobile' },
    { text: 'Share content and build audience', value: 'content' },
    { text: 'Improve user experience', value: 'design' }]

  },
  {
    id: 3,
    question: 'Do you need content management capabilities?',
    options: [
    { text: 'Yes, I need to update content frequently', value: 'content' },
    { text: 'Yes, for product management', value: 'ecommerce' },
    { text: 'No, static content is fine', value: 'webapp' },
    { text: 'Not sure yet', value: 'design' }]

  }];


  const technologies: Technology[] = [
  { name: 'React', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1e23135e2-1764224770237.png", iconAlt: 'React logo - blue atom symbol', proficiency: 95, category: 'Frontend' },
  { name: 'Next.js', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_14f1d5951-1764224770548.png", iconAlt: 'Next.js logo - black N letter', proficiency: 90, category: 'Frontend' },
  { name: 'TypeScript', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_199a42569-1764224772124.png", iconAlt: 'TypeScript logo - blue TS letters', proficiency: 92, category: 'Frontend' },
  { name: 'Tailwind CSS', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb8eda6f-1764224772137.png", iconAlt: 'Tailwind CSS logo - teal wave symbol', proficiency: 88, category: 'Frontend' },
  { name: 'Node.js', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_133a7172a-1764224771314.png", iconAlt: 'Node.js logo - green hexagon', proficiency: 85, category: 'Backend' },
  { name: 'Express', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1402238d8-1764224769752.png", iconAlt: 'Express.js logo - black text', proficiency: 87, category: 'Backend' },
  { name: 'MongoDB', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffb2b62e-1764224772065.png", iconAlt: 'MongoDB logo - green leaf symbol', proficiency: 83, category: 'Backend' },
  { name: 'PostgreSQL', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1af2cf458-1764224771303.png", iconAlt: 'PostgreSQL logo - blue elephant', proficiency: 80, category: 'Backend' },
  { name: 'React Native', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_14132346c-1764224772133.png", iconAlt: 'React Native logo - blue atom symbol', proficiency: 88, category: 'Mobile' },
  { name: 'Flutter', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_150412908-1764224772094.png", iconAlt: 'Flutter logo - blue F letter', proficiency: 82, category: 'Mobile' },
  { name: 'WordPress', icon: "https://images.unsplash.com/photo-1705904506592-d8a0d5392c66", iconAlt: 'WordPress logo - blue W in circle', proficiency: 90, category: 'CMS' },
  { name: 'Shopify', icon: "https://img.rocket.new/generatedImages/rocket_gen_img_12a23a1af-1764224770546.png", iconAlt: 'Shopify logo - green shopping bag', proficiency: 85, category: 'E-commerce' }];


  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Sarah Johnson',
    role: 'CEO',
    company: 'TechStart Inc',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    imageAlt: 'Professional woman with blonde hair in navy blazer smiling at camera in modern office',
    rating: 5,
    text: 'vidhm Solutions transformed our outdated website into a modern, high-performing platform. The team was professional, responsive, and delivered beyond our expectations.',
    service: 'Web Development'
  },
  {
    id: 2,
    name: 'Michael Chen',
    role: 'Founder',
    company: 'FitLife App',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0486763-1763298848491.png",
    imageAlt: 'Asian man with short black hair in white shirt smiling confidently against gray background',
    rating: 5,
    text: 'The mobile app they developed exceeded all our requirements. User engagement increased by 200% within the first month of launch. Highly recommended!',
    service: 'Mobile Development'
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    role: 'Marketing Director',
    company: 'Fashion Forward',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11504e941-1763295994346.png",
    imageAlt: 'Hispanic woman with long brown hair in red blazer smiling warmly in bright office setting',
    rating: 5,
    text: 'Our Shopify store looks amazing and functions flawlessly. Sales have increased by 150% since the redesign. The team truly understands e-commerce.',
    service: 'Shopify Development'
  }];


  const categories = [
  { id: 'all', name: 'All Services', icon: 'Squares2X2Icon' },
  { id: 'development', name: 'Development', icon: 'CodeBracketIcon' },
  { id: 'cms', name: 'CMS', icon: 'DocumentTextIcon' },
  { id: 'ecommerce', name: 'E-commerce', icon: 'ShoppingCartIcon' },
  { id: 'design', name: 'Design', icon: 'PaintBrushIcon' },
  { id: 'marketing', name: 'Marketing', icon: 'ChartBarIcon' }];


  const filteredServices = selectedCategory === 'all' ?
  services :
  services.filter((service) => service.category === selectedCategory);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:363:6" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="363" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:364:8" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="364" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-24%20pb-16%20px-6%20lg%3Apx-8%22%7D" className="pt-24 pb-16 px-6 lg:px-8">
          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:365:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="365" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
            <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:366:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="366" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
              <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:367:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="367" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%20w-1%2F3%22%7D" className="h-12 bg-muted rounded w-1/3" />
              <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:368:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="368" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20w-2%2F3%22%7D" className="h-6 bg-muted rounded w-2/3" />
              <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:369:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="369" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) =>
                <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:371:16" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="371" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" key={i} className="h-96 bg-muted rounded-lg" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:382:4" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="382" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:383:6" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="383" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22pt-24%20pb-16%20px-6%20lg%3Apx-8%22%7D" className="pt-24 pb-16 px-6 lg:px-8">
        <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:384:8" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="384" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:385:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="385" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h1 data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:386:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="386" data-component-file="ServicesInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-bold%20text-text-primary%20mb-4%22%2C%22textContent%22%3A%22Our%20Services%22%7D" className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Our Services
            </h1>
            <p data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:389:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="389" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-text-secondary%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Comprehensive%20digital%20solutions%20tailored%20to%20transform%20your%20business%20ideas%20into%20powerful%2C%20scalable%20applications%22%7D" className="text-lg text-text-secondary max-w-3xl mx-auto">
              Comprehensive digital solutions tailored to transform your business ideas into powerful, scalable applications
            </p>
          </div>

          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:394:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="394" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
            <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:395:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="395" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%20justify-center%22%7D" className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) =>
              <button data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:397:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="397" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-base ${
                selectedCategory === category.id ?
                'bg-primary text-primary-foreground shadow-md' :
                'bg-muted text-text-secondary hover:bg-border'}`
                }>

                  <Icon data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:406:18" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="406" data-component-file="ServicesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={category.icon as any} size={20} />
                  <span data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:407:18" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="407" data-component-file="ServicesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{category.name}</span>
                </button>
              )}
            </div>
          </div>

          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:413:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="413" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:414:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="414" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20border-b%20border-border%20mb-6%22%7D" className="flex border-b border-border mb-6">
              <button data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:415:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="415" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Service%20Catalog%22%7D"
                onClick={() => setActiveTab('services')}
                className={`px-6 py-3 font-semibold transition-all duration-base ${
                activeTab === 'services' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-text-primary'}`
                }>

                Service Catalog
              </button>
              <button data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:423:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="423" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Compare%20Services%22%7D"
                onClick={() => setActiveTab('comparison')}
                className={`px-6 py-3 font-semibold transition-all duration-base ${
                activeTab === 'comparison' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-text-primary'}`
                }>

                Compare Services
              </button>
              <button data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:431:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="431" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Our%20Process%22%7D"
                onClick={() => setActiveTab('process')}
                className={`px-6 py-3 font-semibold transition-all duration-base ${
                activeTab === 'process' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-text-primary'}`
                }>

                Our Process
              </button>
            </div>

            {activeTab === 'services' &&
            <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:442:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="442" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredServices.map((service) =>
              <ServiceCard data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:444:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="444" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%7D" key={service.id} service={service} />
              )}
              </div>
            }

            {activeTab === 'comparison' &&
            <ServiceComparison data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:450:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="450" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceComparison" data-component-content="%7B%22elementName%22%3A%22ServiceComparison%22%7D" features={comparisonFeatures} />
            }

            {activeTab === 'process' &&
            <ProcessWorkflow data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:454:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="454" data-component-file="ServicesInteractive.tsx" data-component-name="ProcessWorkflow" data-component-content="%7B%22elementName%22%3A%22ProcessWorkflow%22%7D" steps={workflowSteps} />
            }
          </div>

          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:458:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="458" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-2%20gap-8%20mb-12%22%7D" className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <ServiceRecommendation data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:459:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="459" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceRecommendation" data-component-content="%7B%22elementName%22%3A%22ServiceRecommendation%22%7D" questions={quizQuestions} />
            <TechnologyStack data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:460:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="460" data-component-file="ServicesInteractive.tsx" data-component-name="TechnologyStack" data-component-content="%7B%22elementName%22%3A%22TechnologyStack%22%7D" technologies={technologies} />
          </div>

          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:463:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="463" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%22%7D" className="mb-12">
            <h2 data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:464:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="464" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-text-primary%20mb-6%20text-center%22%2C%22textContent%22%3A%22What%20Our%20Clients%20Say%22%7D" className="text-3xl font-bold text-text-primary mb-6 text-center">
              What Our Clients Say
            </h2>
            <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:467:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="467" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial) =>
              <TestimonialCard data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:469:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="469" data-component-file="ServicesInteractive.tsx" data-component-name="TestimonialCard" data-component-content="%7B%22elementName%22%3A%22TestimonialCard%22%7D" key={testimonial.id} testimonial={testimonial} />
              )}
            </div>
          </div>

          <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:474:10" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="474" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gradient-to-r%20from-primary%20to-brand-primary%20rounded-lg%20p-8%20md%3Ap-12%20text-center%22%7D" className="bg-gradient-to-r from-primary to-brand-primary rounded-lg p-8 md:p-12 text-center">
            <h2 data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:475:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="475" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-white%20mb-4%22%2C%22textContent%22%3A%22Ready%20to%20Start%20Your%20Project%3F%22%7D" className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:478:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="478" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-white%2F90%20text-lg%20mb-6%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Let's%20discuss%20how%20we%20can%20help%20transform%20your%20ideas%20into%20powerful%20digital%20solutions%22%7D" className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
              Let's discuss how we can help transform your ideas into powerful digital solutions
            </p>
            <div data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:481:12" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="481" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-4%20justify-center%22%7D" className="flex flex-col sm:flex-row gap-4 justify-center">
              <button data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:482:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="482" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-8%20py-3%20text-base%20font-semibold%20text-primary%20bg-white%20hover%3Abg-gray-100%20rounded-md%20shadow-md%20hover%3Ashadow-lg%20hover%3A-translate-y-0.5%20transition-all%20duration-base%22%2C%22textContent%22%3A%22Schedule%20Consultation%22%7D" className="px-8 py-3 text-base font-semibold text-primary bg-white hover:bg-gray-100 rounded-md shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-base">
                Schedule Consultation
              </button>
              <button data-component-id="src/app/services-overview/components/ServicesInteractive.tsx:485:14" data-component-path="src/app/services-overview/components/ServicesInteractive.tsx" data-component-line="485" data-component-file="ServicesInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-8%20py-3%20text-base%20font-semibold%20text-white%20bg-transparent%20border-2%20border-white%20hover%3Abg-white%2F10%20rounded-md%20transition-all%20duration-base%22%2C%22textContent%22%3A%22View%20Portfolio%22%7D" className="px-8 py-3 text-base font-semibold text-white bg-transparent border-2 border-white hover:bg-white/10 rounded-md transition-all duration-base">
                View Portfolio
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>);

}